import {Evented} from 'mapbox-gl';
import initializeOptions from 'Map/configurer/initializeTrafficIncidentsOptions';
import trafficIncidentDetailsTier from 'Map/tier/trafficIncidentDetailsTier';
import trafficIncidentTilesTier from 'Map/tier/trafficIncidentTilesTier';

const POSITION_TRAFFIC_INCIDENTS = 3;
export const ID = 'trafficIncidents';

/**
 * This tier provides both
 * <a href="https://developer.tomtom.com/traffic-api/traffic-api-documentation/traffic-incidents">traffic incidents</a>
 *  details and tiles information.
 *
 * ```javascript
 * var map = tt.map({
 *  //your map configuration
 *});
 * map.on('load', function() {
 *     map.addTier(new tt.TrafficIncidentTier({
 *         key: <api key>,//your traffic incidents api key goes here
 *         incidentDetails: true, //or provide more detailed configuration
 *         incidentTiles: true //or provide more detailed configuration
 *     }))
 * });
 *```
 *
 * @class TrafficIncidentTier
 * @namespace Maps
 */

export class TrafficIncidentTier extends Evented {
    // eslint-disable
    /**
     * @method constructor
     *
     * @param {Object} options Traffic incidents options.
     * @param {String} options.key Traffic incident api key. This key is shared between traffic incident details and
     * tiles services.
     * @param {Object} [options.incidentDetails] All options for Incident Details service, which are documented
     * <a href="TRAFFIC_INCIDENT_DETAILS_URL">here</a>. Additional options can be found below.
     * @param {String} [options.incidentDetails.style] Style of traffic incidents details (may change their position).
     * Supported values are: `s0`, `s0-dark`, `s1`, `s2` and `s3`.
     * @param {String} [options.incidentDetails.markerOptions.units = 'metric'] Measurements units. Supported values:
     * 'metric', 'imperial'.
     * @param {Function} [options.incidentDetails.markerOptions.markerFactory] A factory method for creating marker
     * instances. This method may have two parameters:
     * * a feature representing incident (regular or clustered)
     * * a default factory method which may be invoked to build a default marker for a given incident.
     * * an optional instance of an old marker which can be updated
     *
     * This method needs to return a {{#crossLink "Maps.Marker"}}{{/crossLink}} instance or null. The latter will
     * cause that given incident will not be rendered on a map.
     * @param {Boolean} [options.incidentDetails.markerOptions.cluster.disableOnClickBehavior = false] The flag which
     * allows you to disable default on 'click' behavior for cluster markers
     * @param {Boolean} [options.incidentDetails.cssStyleCheck=true] If true, the tier will check if required css
     * styles are added.
     * @param {String} [options.incidentTiles.style='tomtom://vector/1/s1'] The traffic incident tiles style.
     * It can be one of following types:
     * * Schema, you can use of following values:
     *   1. `tomtom://vector/1/s0`
     *   2. `tomtom://vector/1/s0-dark`
     *   3. `tomtom://vector/1/s1`
     *   4. `tomtom://vector/1/s2`
     *   5. `tomtom://vector/1/s3`
     *
     *   6. `tomtom://raster/1/s0`
     *   7. `tomtom://raster/1/s0-dark`
     *   8. `tomtom://raster/1/s1`
     *   9. `tomtom://raster/1/s2`
     *   10. `tomtom://raster/1/s3`
     * * URL to the JSON object conforming
     * to the schema described in the
     * <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification">
     * Map Style Specification</a>.
     * * JSON object conforming to the mentioned specification.
     * @param {Number} [options.refresh=30000] The interval in milliseconds between subsequent refreshes of traffic
     * incident information. Any value lower than 30000 will be treated as 30000. This interval affects both traffic
     * incident details and tiles services.
     */
    // eslint-disable
    constructor(options, position = POSITION_TRAFFIC_INCIDENTS) {
        super();
        this._options = initializeOptions(options);
        this._position = position;
        this._tilesTier = this._options.incidentTiles ? trafficIncidentTilesTier(this._options, position) : null;
        this._detailsTier = this._options.incidentDetails ?
            trafficIncidentDetailsTier(this._options, position, this) : null;
        this._visible = false;
    }

    /**
     * @method getId
     * @description Returns tier's ID
     * @return {String}
     */
    getId = () => ID;
    getPosition = () => this._position;
    getStyle = () => this._tilesTier && this._tilesTier.getStyle();
    setStyle = (style) => {
        if (this._tilesTier) {
            this._tilesTier.setStyle(style);
        }
    };
    getOptions = () => this._options;
    isVisible = () => this._visible;

    onBeforeAdd = (map) => {
        if (this._tilesTier) {
            this._tilesTier.onBeforeAdd(map);
        }
    };

    onAdd = (map) => {
        if (this._tilesTier) {
            this._tilesTier.onAdd(map);
        }

        if (this._detailsTier) {
            this._detailsTier.onAdd(map);
        }
    };

    onRemove = (map) => {
        if (this._tilesTier) {
            this._tilesTier.onRemove(map);
        }

        if (this._detailsTier) {
            this._detailsTier.onRemove(map);
        }
    };

    getUrlTransformer = () => this._tilesTier && this._tilesTier.getUrlTransformer();
}

/**
 * Fired immediately after incidents are fetched and a map is updated. An event instance contains following fields:
 * * trafficIncidents - data returned by the traffic incident service
 * * markers - an object where keys are ids of incidents and values are markers
 * (instances of {{#crossLink "Maps.Marker"}}{{/crossLink}}) representing those incidents on a map.
 * @event tomtom.trafficIncidents.detailsUpdated
 */



// WEBPACK FOOTER //
// ./map/tier/trafficIncidentTier.js